import type { AimConversationSummary } from '../../lib/api.js';

// Lane D2 (D5) — prior-conversation list for the AIM page. Ported from
// the legacy AimDrawer interaction model (production/happy-tail
// components/aim) onto the platform admin-app's ferro-* design system —
// design reference only, no CSS/branding copied.

type AimConversationListProps = {
  conversations: AimConversationSummary[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onArchive: (id: string) => void;
  loading: boolean;
  /** List load failed — render the error state, never the empty state. */
  error?: boolean;
  /** Recovery action for the error state (design constitution: every
   *  error says how to recover). */
  onRetry?: () => void;
};

function formatWhen(iso: string): string {
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    return sameDay
      ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
      : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

export function AimConversationList({
  conversations,
  activeId,
  onSelect,
  onNew,
  onArchive,
  loading,
  error = false,
  onRetry,
}: AimConversationListProps): JSX.Element {
  return (
    <aside className="ferro-aim__sidebar" aria-label="Prior conversations">
      <button
        type="button"
        className="ferro-btn ferro-btn--primary ferro-aim__new"
        onClick={onNew}
        data-testid="aim-new-chat"
      >
        + New chat
      </button>
      {loading ? (
        <p className="ferro-aim__sidebar-empty">Loading…</p>
      ) : error ? (
        <div className="ferro-aim__sidebar-empty" role="alert" data-testid="aim-list-error">
          <p>Couldn't load your conversations.</p>
          {onRetry ? (
            <button
              type="button"
              className="ferro-btn ferro-btn--sm ferro-btn--ghost"
              onClick={onRetry}
            >
              Try again
            </button>
          ) : null}
        </div>
      ) : conversations.length === 0 ? (
        <p className="ferro-aim__sidebar-empty">No conversations yet.</p>
      ) : (
        <ul className="ferro-aim__conv-list">
          {conversations.map((c) => (
            <li key={c.id} className="ferro-aim__conv-item">
              <button
                type="button"
                className={`ferro-aim__conv-btn${c.id === activeId ? ' ferro-aim__conv-btn--active' : ''}`}
                onClick={() => onSelect(c.id)}
                data-testid={`aim-conv-${c.id}`}
              >
                <span className="ferro-aim__conv-title">
                  {c.title ?? 'Untitled conversation'}
                </span>
                <span className="ferro-aim__conv-when">{formatWhen(c.updatedAt)}</span>
              </button>
              <button
                type="button"
                className="ferro-aim__conv-archive"
                aria-label={`Archive conversation: ${c.title ?? 'untitled'}`}
                title="Archive"
                onClick={() => onArchive(c.id)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
